import { useState, useEffect, useRef, Fragment } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const STATUS_RANK = { inactive: 0, pending: 1, active: 2 };
function statusKey(u) {
  if (u.is_active === false) return 'inactive';
  return u.must_change_password ? 'pending' : 'active';
}
function compareUsers(a, b, sortKey, sortDir) {
  let cmp = 0;
  if (sortKey === 'name') cmp = a.name.localeCompare(b.name);
  else if (sortKey === 'email') cmp = a.email.localeCompare(b.email);
  else if (sortKey === 'lunches') cmp = a.matches.length - b.matches.length;
  else if (sortKey === 'status') cmp = STATUS_RANK[statusKey(a)] - STATUS_RANK[statusKey(b)];
  return sortDir === 'asc' ? cmp : -cmp;
}

function SortableTh({ label, sortKey, currentKey, dir, onSort, style }) {
  const active = currentKey === sortKey;
  return (
    <th
      style={{ ...th, ...style, cursor: 'pointer', userSelect: 'none' }}
      onClick={() => onSort(sortKey)}
    >
      {label}
      <span style={{ marginLeft: 4, opacity: active ? 1 : 0.25 }}>{active && dir === 'desc' ? '▼' : '▲'}</span>
    </th>
  );
}

function InviteTab() {
  const [emailsText, setEmailsText] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const pollStatus = (jobId) => {
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/admin/invite/status/${jobId}`);
        setProgress({ processed: res.data.processed, total: res.data.total });
        if (res.data.status === 'done') {
          clearInterval(interval);
          setResults(res.data.results);
          setProgress(null);
          setLoading(false);
        }
      } catch {
        // transient network hiccup — keep polling, don't fail the whole batch over one missed poll
      }
    }, 1500);
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setError('');
    setResults(null);
    const entries = emailsText.split('\n').map(line => {
      line = line.trim();
      if (!line) return null;
      const commaIdx = line.indexOf(',');
      if (commaIdx !== -1) {
        const name = line.slice(0, commaIdx).trim();
        const email = line.slice(commaIdx + 1).trim();
        return { name: name || null, email };
      }
      return { name: null, email: line };
    }).filter(Boolean);
    if (entries.length === 0) { setError('Please enter at least one email address.'); return; }
    setLoading(true);
    try {
      const res = await api.post('/admin/invite', { entries });
      setProgress({ processed: 0, total: res.data.total });
      setEmailsText('');
      pollStatus(res.data.jobId);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send invitations.');
      setLoading(false);
    }
  };

  return (
    <>
      <div className="card" style={{ padding: 32 }}>
        <form onSubmit={handleInvite} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
              EMAIL ADDRESSES
            </label>
            <textarea
              className="input"
              style={{ height: 180, resize: 'vertical', fontFamily: 'monospace', fontSize: 14 }}
              placeholder={'Alice Smith, alice@example.com\nbob@example.com\nCarol Yamamoto, carol@example.com'}
              value={emailsText}
              onChange={e => setEmailsText(e.target.value)}
              required
            />
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
              One entry per line. Optionally prefix with a name: <code>Name, email@example.com</code>
            </p>
          </div>
          {error && (
            <div style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', fontSize: 14 }}>
              {error}
            </div>
          )}
          <button type="submit" className="btn btn-primary" style={{ justifyContent: 'center', padding: '12px' }} disabled={loading}>
            {loading
              ? (progress ? `Sending ${progress.processed} / ${progress.total}…` : 'Sending invitations…')
              : 'Send Invitations'}
          </button>
        </form>
      </div>

      {results && (
        <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {results.invited?.length > 0 && (
            <div className="card" style={{ padding: 20 }}>
              <h3 style={{ color: '#15803d', margin: '0 0 10px', fontSize: 15 }}>Invited ({results.invited.length})</h3>
              <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {results.invited.map(email => <li key={email} style={{ fontSize: 14 }}>{email}</li>)}
              </ul>
            </div>
          )}
          {results.skipped?.length > 0 && (
            <div className="card" style={{ padding: 20 }}>
              <h3 style={{ color: '#92400e', margin: '0 0 10px', fontSize: 15 }}>Skipped — already registered ({results.skipped.length})</h3>
              <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {results.skipped.map(({ email, reason }) => (
                  <li key={email} style={{ fontSize: 14 }}>{email} <span style={{ color: 'var(--text-muted)' }}>— {reason}</span></li>
                ))}
              </ul>
            </div>
          )}
          {results.errors?.length > 0 && (
            <div className="card" style={{ padding: 20 }}>
              <h3 style={{ color: '#dc2626', margin: '0 0 10px', fontSize: 15 }}>Errors ({results.errors.length})</h3>
              <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {results.errors.map(({ email, reason }) => (
                  <li key={email} style={{ fontSize: 14 }}>{email} <span style={{ color: '#dc2626' }}>— {reason}</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    api.get('/admin/users')
      .then(res => setUsers(res.data))
      .catch(() => setError('Failed to load users.'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const handleSort = (key) => {
    if (key === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sortedUsers = [...users].sort((a, b) => compareUsers(a, b, sortKey, sortDir));

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: 16 }}>Loading…</div>;
  if (error) return <div style={{ color: '#dc2626', padding: 16 }}>{error}</div>;

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: ADMIN_BG, borderBottom: `1px solid ${ADMIN_BORDER}` }}>
            <SortableTh label="Name" sortKey="name" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
            <SortableTh label="Email" sortKey="email" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
            <SortableTh label="Lunches" sortKey="lunches" currentKey={sortKey} dir={sortDir} onSort={handleSort} style={{ textAlign: 'center' }} />
            <SortableTh label="Status" sortKey="status" currentKey={sortKey} dir={sortDir} onSort={handleSort} style={{ textAlign: 'center' }} />
            <th style={{ ...th, width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {sortedUsers.map((u, i) => (
            <Fragment key={u.id}>
              <tr
                onClick={() => u.matches.length > 0 && toggle(u.id)}
                style={{
                  borderBottom: expanded[u.id] ? 'none' : '1px solid var(--border)',
                  background: i % 2 === 0 ? 'white' : '#fafafa',
                  cursor: u.matches.length > 0 ? 'pointer' : 'default',
                }}
              >
                <td style={td}>
                  <span style={{ fontWeight: 500 }}>{u.name}</span>
                  {u.is_admin && <span style={{ marginLeft: 6, fontSize: 11, background: '#dcfce7', color: '#15803d', borderRadius: 4, padding: '1px 6px', fontWeight: 600 }}>admin</span>}
                </td>
                <td style={{ ...td, color: 'var(--text-muted)', fontSize: 13 }}>{u.email}</td>
                <td style={{ ...td, textAlign: 'center', fontWeight: 600 }}>{u.matches.length}</td>
                <td style={{ ...td, textAlign: 'center' }}>
                  {u.must_change_password
                    ? <span style={{ fontSize: 11, background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '2px 8px' }}>pending setup</span>
                    : <span style={{ fontSize: 11, background: '#dcfce7', color: '#15803d', borderRadius: 4, padding: '2px 8px' }}>active</span>
                  }
                </td>
                <td style={{ ...td, textAlign: 'center', color: 'var(--text-muted)' }}>
                  {u.matches.length > 0 && (expanded[u.id] ? '▲' : '▼')}
                </td>
              </tr>
              {expanded[u.id] && (
                <tr key={`${u.id}-matches`} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td colSpan={5} style={{ padding: '0 0 12px 0', background: '#f0fdf4' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #bbf7d0' }}>
                          <th style={{ ...th, fontSize: 11, color: 'var(--text-muted)', paddingLeft: 40 }}>Date</th>
                          <th style={{ ...th, fontSize: 11, color: 'var(--text-muted)' }}>Lunch with</th>
                          <th style={{ ...th, fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {u.matches.map(m => (
                          <tr key={m.id}>
                            <td style={{ ...td, paddingLeft: 40, fontSize: 13, color: 'var(--text-muted)' }}>
                              {new Date(m.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </td>
                            <td style={{ ...td, fontSize: 14 }}>
                              {m.partners.map(p => p.name).join(', ')}
                            </td>
                            <td style={{ ...td, textAlign: 'center' }}>
                              <span style={{
                                fontSize: 11, borderRadius: 4, padding: '2px 8px',
                                background: m.status === 'completed' ? '#f0f9ff' : m.status === 'cancelled' ? '#fef2f2' : '#f0fdf4',
                                color: m.status === 'completed' ? '#0369a1' : m.status === 'cancelled' ? '#dc2626' : '#15803d'
                              }}>{m.status}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
      {users.length === 0 && (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>No users yet.</div>
      )}
    </div>
  );
}

function toLocalDateString(d) {
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function getUpcomingMondays(count = 8) {
  const mondays = [];
  const now = new Date();
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const isMondayAfternoon = day === 1 && now.getHours() >= 12;
  const daysUntilMonday = day === 1 ? (isMondayAfternoon ? 7 : 0) : day === 0 ? 1 : 8 - day;
  d.setDate(d.getDate() + daysUntilMonday);
  for (let i = 0; i < count; i++) {
    mondays.push(new Date(d));
    d.setDate(d.getDate() + 7);
  }
  return mondays;
}

function weekdayShort(dateStr) {
  return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' });
}

function UnmatchedChip({ user, details }) {
  const [hovered, setHovered] = useState(false);
  const hideTimer = useRef(null);
  const email = user.email || details?.email;

  const showTooltip = () => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
    setHovered(true);
  };
  const scheduleHide = () => {
    hideTimer.current = setTimeout(() => setHovered(false), 1000);
  };
  useEffect(() => () => { if (hideTimer.current) clearTimeout(hideTimer.current); }, []);

  return (
    <span
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={showTooltip}
      onMouseLeave={scheduleHide}
    >
      <span style={{
        fontSize: 12, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6,
        padding: '2px 8px', color: '#92400e', cursor: details ? 'help' : 'default'
      }}>
        {user.name} (unmatched)
      </span>
      {hovered && details && (
        <div style={{
          position: 'absolute', zIndex: 20, top: '100%', left: 0, marginTop: 6,
          background: 'white', border: '1px solid var(--border)', borderRadius: 8,
          padding: 12, minWidth: 240, boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          fontSize: 12, color: 'var(--text)', textAlign: 'left', whiteSpace: 'normal'
        }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>{user.name}</div>
          <div style={{ marginBottom: 4 }}>
            <b>Available this week:</b>{' '}
            {details.availability.length ? details.availability.map(weekdayShort).join(', ') : 'none'}
          </div>
          <div style={{ marginBottom: 4 }}>
            <b>Email:</b>{' '}
            {email ? (
              <a href={`mailto:${email}`} style={{ color: '#2563eb', textDecoration: 'underline' }}>{email}</a>
            ) : '—'}
          </div>
          <div style={{ marginBottom: 4 }}>
            <b>Group size:</b> {details.groupSizePreferences.join(', ')}
          </div>
          <div style={{ marginBottom: 4 }}>
            <b>Max lunches/week:</b> {details.maxLunchesPerWeek}
          </div>
          <div style={{ marginBottom: 4 }}>
            <b>Locations:</b> {details.locations.map(l => `${l.flag ? l.flag + ' ' : ''}${l.name}`).join(', ') || '—'}
          </div>
          <div style={{ marginBottom: 4 }}>
            <b>Languages:</b> {details.languages.map(l => l.name).join(', ') || '—'}
          </div>
          <div style={{ marginBottom: 4 }}>
            <b>Business unit:</b> {details.myBusinessUnits.map(b => `${b.emoji ? b.emoji + ' ' : ''}${b.name}`).join(', ') || '—'}
          </div>
          <div>
            <b>Interested in:</b> {details.interestedBusinessUnits.map(b => `${b.emoji ? b.emoji + ' ' : ''}${b.name}`).join(', ') || '—'}
          </div>
        </div>
      )}
    </span>
  );
}

function DryRunTab() {
  const mondays = getUpcomingMondays(8);
  const [date, setDate] = useState(toLocalDateString(mondays[0]));
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const run = async (selectedMonday) => {
    setError(''); setResult(null); setLoading(true);
    try {
      const res = await api.post('/admin/match-preview-week', { monday: selectedMonday });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to run preview.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="card" style={{ padding: 32 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 12 }}>
          SELECT A MONDAY TO PREVIEW
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {mondays.map(m => {
            const val = toLocalDateString(m);
            const label = m.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
            const isSelected = date === val;
            return (
              <button
                key={val}
                type="button"
                onClick={() => { setDate(val); setResult(null); setError(''); }}
                style={{
                  padding: '8px 16px', borderRadius: 8, border: '1px solid',
                  borderColor: isSelected ? 'var(--match-a)' : 'var(--border)',
                  background: isSelected ? 'var(--match-a)' : 'white',
                  color: isSelected ? 'white' : 'var(--text)',
                  fontFamily: 'DM Sans', fontSize: 14, fontWeight: isSelected ? 600 : 400,
                  cursor: 'pointer'
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="btn btn-primary"
          style={{ marginTop: 20, padding: '10px 24px' }}
          disabled={loading}
          onClick={() => run(date)}
        >
          {loading ? 'Running…' : 'Preview Matches'}
        </button>
        {error && <div style={{ marginTop: 16, color: '#dc2626', fontSize: 14 }}>{error}</div>}
      </div>

      {result && (
        <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>
            Dry run for the following week — nothing was saved and no emails were sent.
          </div>
          {result.byDate.map(({ date: d, matched, unmatched }) => (
            <div key={d} className="card" style={{ padding: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 10 }}>
                {new Date(d + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })}
              </div>
              {matched.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {matched.map((m, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0' }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#15803d', minWidth: 24 }}>{m.groupSize === 2 ? '👥' : m.groupSize === 3 ? '👥👤' : '👥👥'}</span>
                      <span style={{ fontWeight: 500, flex: 1 }}>{m.members.map(u => u.name).join(' · ')}</span>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)', background: 'white', borderRadius: 6, padding: '2px 8px', border: '1px solid var(--border)' }}>score {m.score}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No matches</div>
              )}
              {unmatched.length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {unmatched.map(u => (
                    <UnmatchedChip key={u.id} user={u} details={result.unmatchedDetails?.[u.id]} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function ManageTab() {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.email === 'mfrey@ieee.org';
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState({});
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (key) => {
    if (key === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const load = () => {
    setLoading(true);
    api.get('/admin/users')
      .then(res => setUsers(res.data))
      .catch(() => setError('Failed to load users.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const setUserBusy = (id, val) => setBusy(b => ({ ...b, [id]: val }));

  const deactivate = async (id) => {
    setUserBusy(id, true);
    try {
      await api.patch(`/admin/users/${id}/deactivate`);
      setUsers(us => us.map(u => u.id === id ? { ...u, is_active: false } : u));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to deactivate user.');
    } finally {
      setUserBusy(id, false);
    }
  };

  const activate = async (id) => {
    setUserBusy(id, true);
    try {
      await api.patch(`/admin/users/${id}/activate`);
      setUsers(us => us.map(u => u.id === id ? { ...u, is_active: true } : u));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to activate user.');
    } finally {
      setUserBusy(id, false);
    }
  };

  const toggleAdmin = async (id) => {
    setUserBusy(id, true);
    try {
      const res = await api.patch(`/admin/users/${id}/toggle-admin`);
      setUsers(us => us.map(u => u.id === id ? { ...u, is_admin: res.data.is_admin } : u));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update admin status.');
    } finally {
      setUserBusy(id, false);
    }
  };

  const deleteUser = async (id) => {
    setUserBusy(id, true);
    try {
      await api.delete(`/admin/users/${id}`);
      setUsers(us => us.filter(u => u.id !== id));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete user.');
    } finally {
      setUserBusy(id, false);
      setConfirmDelete(null);
    }
  };

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: 16 }}>Loading…</div>;
  if (error) return <div style={{ color: '#dc2626', padding: 16 }}>{error}</div>;

  const sortedUsers = [...users].sort((a, b) => compareUsers(a, b, sortKey, sortDir));

  return (
    <>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: ADMIN_BG, borderBottom: `1px solid ${ADMIN_BORDER}` }}>
              <SortableTh label="Name" sortKey="name" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
              <SortableTh label="Email" sortKey="email" currentKey={sortKey} dir={sortDir} onSort={handleSort} style={{ maxWidth: 160 }} />
              <SortableTh label="Lunches" sortKey="lunches" currentKey={sortKey} dir={sortDir} onSort={handleSort} style={{ textAlign: 'center' }} />
              <SortableTh label="Status" sortKey="status" currentKey={sortKey} dir={sortDir} onSort={handleSort} style={{ textAlign: 'center' }} />
              <th style={{ ...th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map((u, i) => (
              <tr key={u.id} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                <td style={td}>
                  <span style={{ fontWeight: 500, color: u.is_active ? 'var(--text)' : 'var(--text-muted)', textDecoration: u.is_active ? 'none' : 'line-through' }}>{u.name}</span>
                  {u.is_admin && <span style={{ marginLeft: 6, fontSize: 11, background: '#dcfce7', color: '#15803d', borderRadius: 4, padding: '1px 6px', fontWeight: 600 }}>admin</span>}
                </td>
                <td style={{ ...td, color: 'var(--text-muted)', fontSize: 13, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={u.email}>{u.email}</td>
                <td style={{ ...td, textAlign: 'center', fontWeight: 600 }}>{u.matches.length}</td>
                <td style={{ ...td, textAlign: 'center' }}>
                  {!u.is_active
                    ? <span style={{ fontSize: 11, background: '#fef2f2', color: '#dc2626', borderRadius: 4, padding: '2px 8px' }}>inactive</span>
                    : u.must_change_password
                      ? <span style={{ fontSize: 11, background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '2px 8px' }}>pending setup</span>
                      : <span style={{ fontSize: 11, background: '#dcfce7', color: '#15803d', borderRadius: 4, padding: '2px 8px' }}>active</span>
                  }
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    {isSuperAdmin && (
                      <button
                        className="btn btn-ghost"
                        style={{ fontSize: 13, padding: '4px 12px', color: u.is_admin ? '#92400e' : '#15803d', whiteSpace: 'nowrap' }}
                        disabled={busy[u.id]}
                        onClick={() => toggleAdmin(u.id)}
                      >{u.is_admin ? 'Remove Admin' : 'Make Admin'}</button>
                    )}
                    {u.is_active
                      ? <button
                          className="btn btn-ghost"
                          style={{ fontSize: 13, padding: '4px 12px', color: '#92400e', whiteSpace: 'nowrap' }}
                          disabled={busy[u.id]}
                          onClick={() => deactivate(u.id)}
                        >Deactivate</button>
                      : <button
                          className="btn btn-ghost"
                          style={{ fontSize: 13, padding: '4px 12px', color: '#15803d', whiteSpace: 'nowrap' }}
                          disabled={busy[u.id]}
                          onClick={() => activate(u.id)}
                        >Activate</button>
                    }
                    <button
                      className="btn btn-ghost"
                      style={{ fontSize: 13, padding: '4px 12px', color: '#dc2626', whiteSpace: 'nowrap' }}
                      disabled={busy[u.id]}
                      onClick={() => setConfirmDelete(u)}
                    >Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {users.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>No users.</div>
        )}
      </div>

      {confirmDelete && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="card" style={{ padding: 32, maxWidth: 400, width: '90%' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 18 }}>Delete user?</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 24 }}>
              This will permanently delete <strong>{confirmDelete.name}</strong> ({confirmDelete.email}) and all their data. This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button
                className="btn btn-primary"
                style={{ background: '#dc2626', borderColor: '#dc2626' }}
                disabled={busy[confirmDelete.id]}
                onClick={() => deleteUser(confirmDelete.id)}
              >
                {busy[confirmDelete.id] ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function BarChart({ rows, labelKey, valueKey, color = '#7c4a1e' }) {
  if (!rows || rows.length === 0) return <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No data yet.</div>;
  const max = Math.max(...rows.map(r => Number(r[valueKey])), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 96 }}>
      {rows.map((r, i) => (
        <div key={i} title={`${r[labelKey]}: ${r[valueKey]}`}
             style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 3 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>{r[valueKey]}</span>
          <div style={{ width: '100%', background: color, opacity: 0.75, borderRadius: '3px 3px 0 0',
                        height: `${Math.max(4, (Number(r[valueKey]) / max) * 68)}px`, minHeight: 4 }} />
        </div>
      ))}
    </div>
  );
}

function Stat({ label, value, sub, color }) {
  return (
    <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 20px', flex: 1, minWidth: 120 }}>
      <div style={{ fontSize: 26, fontWeight: 700, color: color || ADMIN_COLOR }}>{value ?? '–'}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function MatchesByWeekPanel({ weeklyMatches, fmtWeek }) {
  const weekOptions = [...weeklyMatches].reverse(); // most recent first
  const [week, setWeek] = useState(weekOptions[0]?.week || '');
  const [matches, setMatches] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!week) return;
    setError(''); setLoading(true);
    api.get('/admin/matches-by-week', { params: { week } })
      .then(res => setMatches(res.data.matches))
      .catch(() => setError('Failed to load matches for that week.'))
      .finally(() => setLoading(false));
  }, [week]);

  const byDate = {};
  for (const m of matches || []) {
    const d = m.matched_date.slice(0, 10);
    (byDate[d] = byDate[d] || []).push(m);
  }

  return (
    <div className="card" style={{ padding: 24 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 15, color: ADMIN_COLOR }}>Matches by week</h3>
      <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--text-muted)' }}>
        Who was actually matched with whom, once that week's matching has run.
      </p>

      {weekOptions.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No completed weeks yet.</div>
      ) : (
        <>
          <select
            value={week}
            onChange={e => setWeek(e.target.value)}
            style={{
              padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)',
              fontFamily: 'DM Sans', fontSize: 14, marginBottom: 16, background: 'white'
            }}
          >
            {weekOptions.map(w => (
              <option key={w.week} value={w.week}>Week of {fmtWeek(w.week)}</option>
            ))}
          </select>

          {loading && <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>}
          {error && <div style={{ color: '#dc2626', fontSize: 13 }}>{error}</div>}

          {!loading && !error && matches && (
            matches.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No matches that week.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {Object.entries(byDate).map(([date, dayMatches]) => (
                  <div key={date}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8 }}>
                      {new Date(date + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {dayMatches.map(m => (
                        <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0' }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#15803d', minWidth: 24 }}>{m.group_size === 2 ? '👥' : m.group_size === 3 ? '👥👤' : '👥👥'}</span>
                          <span style={{ fontWeight: 500, flex: 1 }}>{m.members.map(u => u.name).join(' · ')}</span>
                          <span style={{ fontSize: 12, color: 'var(--text-muted)', background: 'white', borderRadius: 6, padding: '2px 8px', border: '1px solid var(--border)' }}>score {m.score}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}

function UnmatchedByWeekPanel({ weeklyMatches, fmtWeek }) {
  const weekOptions = [...weeklyMatches].reverse(); // most recent first
  const [week, setWeek] = useState(weekOptions[0]?.week || '');
  const [byDate, setByDate] = useState(null);
  const [unmatchedDetails, setUnmatchedDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!week) return;
    setError(''); setLoading(true);
    api.get('/admin/unmatched-by-week', { params: { week } })
      .then(res => { setByDate(res.data.byDate); setUnmatchedDetails(res.data.unmatchedDetails); })
      .catch(() => setError('Failed to load unmatched people for that week.'))
      .finally(() => setLoading(false));
  }, [week]);

  const totalUnmatched = (byDate || []).reduce((n, d) => n + d.unmatched.length, 0);

  return (
    <div className="card" style={{ padding: 24 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 15, color: ADMIN_COLOR }}>Unmatched people by week</h3>
      <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--text-muted)' }}>
        Who was available but not matched, once that week's matching has run.
      </p>

      {weekOptions.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No completed weeks yet.</div>
      ) : (
        <>
          <select
            value={week}
            onChange={e => setWeek(e.target.value)}
            style={{
              padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)',
              fontFamily: 'DM Sans', fontSize: 14, marginBottom: 16, background: 'white'
            }}
          >
            {weekOptions.map(w => (
              <option key={w.week} value={w.week}>Week of {fmtWeek(w.week)}</option>
            ))}
          </select>

          {loading && <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>}
          {error && <div style={{ color: '#dc2626', fontSize: 13 }}>{error}</div>}

          {!loading && !error && byDate && (
            totalUnmatched === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Everyone available that week was matched.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {byDate.filter(d => d.unmatched.length > 0).map(({ date, unmatched }) => (
                  <div key={date}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8 }}>
                      {new Date(date + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {unmatched.map(u => (
                        <UnmatchedChip key={u.id} user={u} details={unmatchedDetails[u.id]} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}

function StatsTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/admin/stats')
      .then(res => setStats(res.data))
      .catch(() => setError('Failed to load statistics.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: 16 }}>Loading…</div>;
  if (error) return <div style={{ color: '#dc2626', padding: 16 }}>{error}</div>;

  const { users, matches, weekly_matches, recent_signups, top_users, daily_access } = stats;

  const fmtWeek = (w) => new Date(String(w).slice(0, 10) + 'T12:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const fmtDay  = (d) => new Date(String(d).slice(0, 10) + 'T12:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Users */}
      <div className="card" style={{ padding: 24 }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 15, color: ADMIN_COLOR }}>Users</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Stat label="Total"         value={users.total} />
          <Stat label="Active"        value={users.active}        color="#15803d" />
          <Stat label="Pending setup" value={users.pending}       color="#92400e" />
          <Stat label="Inactive"      value={users.inactive}      color="#6b7280" />
          <Stat label="New this month" value={users.new_this_month} color="#0369a1" />
        </div>
      </div>

      {/* Matches */}
      <div className="card" style={{ padding: 24 }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 15, color: ADMIN_COLOR }}>Matches</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Stat label="Total"        value={matches.total} />
          <Stat label="This month"   value={matches.this_month}    color="#0369a1" />
          <Stat label="This week"    value={matches.this_week}     color="#15803d" />
          <Stat label="Never matched — signup pending" value={matches.never_matched_pending} color="#92400e" sub="invited, hasn't set up their account yet" />
          <Stat label="Never matched — active" value={matches.never_matched_active} color="#dc2626" sub="set up their account, still 0 lunches" />
        </div>
      </div>

      {/* Weekly match activity */}
      <div className="card" style={{ padding: 24 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 15, color: ADMIN_COLOR }}>Luncheons per week — last 12 weeks</h3>
        <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--text-muted)' }}>
          {weekly_matches.map(r => fmtWeek(r.week)).join(' · ')}
        </p>
        <BarChart rows={weekly_matches} labelKey="week" valueKey="match_count" color="#7c4a1e" />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
          <span>{weekly_matches[0] ? fmtWeek(weekly_matches[0].week) : ''}</span>
          <span>{weekly_matches.at(-1) ? fmtWeek(weekly_matches.at(-1).week) : ''}</span>
        </div>
      </div>

      <MatchesByWeekPanel weeklyMatches={weekly_matches} fmtWeek={fmtWeek} />

      <UnmatchedByWeekPanel weeklyMatches={weekly_matches} fmtWeek={fmtWeek} />

      {/* New signups */}
      <div className="card" style={{ padding: 24 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 15, color: ADMIN_COLOR }}>New signups — last 30 days</h3>
        <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--text-muted)' }}>Daily new accounts created</p>
        <BarChart rows={recent_signups} labelKey="day" valueKey="count" color="#0369a1" />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
          <span>{recent_signups[0] ? fmtDay(recent_signups[0].day) : ''}</span>
          <span>{recent_signups.at(-1) ? fmtDay(recent_signups.at(-1).day) : ''}</span>
        </div>
      </div>

      {/* Access */}
      <div className="card" style={{ padding: 24 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 15, color: ADMIN_COLOR }}>API access — last 30 days</h3>
        <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--text-muted)' }}>Total API requests per day (counts from service start)</p>
        {daily_access.length === 0
          ? <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No data yet — starts accumulating after deploy.</div>
          : <>
              <BarChart rows={daily_access} labelKey="date" valueKey="request_count" color="#15803d" />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
                <span>{fmtDay(daily_access[0].date)}</span>
                <span>{fmtDay(daily_access.at(-1).date)}</span>
              </div>
            </>
        }
      </div>

      {/* Top users */}
      <div className="card" style={{ padding: 24 }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 15, color: ADMIN_COLOR }}>Most active lunchers</h3>
        {top_users.length === 0
          ? <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No matches yet.</div>
          : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {top_users.map((u, i) => {
                const max = Number(top_users[0].match_count);
                const pct = Math.round((Number(u.match_count) / max) * 100);
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', width: 16, textAlign: 'right' }}>{i + 1}</span>
                    <span style={{ fontSize: 14, minWidth: 160 }}>{u.name}</span>
                    <div style={{ flex: 1, background: '#f0f0f0', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: ADMIN_COLOR, borderRadius: 4 }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, minWidth: 24, textAlign: 'right' }}>{u.match_count}</span>
                  </div>
                );
              })}
            </div>
        }
      </div>

    </div>
  );
}

const th = { padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.04em', textTransform: 'uppercase' };
const td = { padding: '12px 16px', fontSize: 14, verticalAlign: 'middle' };

const TABS = ['Invite', 'Dry Run', 'Users', 'Manage', 'Stats'];

const TAB_DESC = {
  'Invite':  'Each address will receive an email with a link to set up their account and choose a password.',
  'Dry Run': 'Preview who would be matched on a given date — nothing is saved and no emails are sent.',
  'Users':   'All members and their lunch history.',
  'Manage':  'Deactivate or permanently delete member accounts.',
  'Stats':   'Usage statistics: users, matches, signups, and daily API access.',
};

const ADMIN_COLOR = '#7c4a1e';
const ADMIN_BG = '#fdf6f0';
const ADMIN_BORDER = '#e8c9a0';

export default function AdminPage() {
  const [tab, setTab] = useState('Invite');

  return (
    <div style={{ maxWidth: 800, margin: '40px auto', padding: '0 24px' }}>
      <h1 style={{ fontSize: 26, marginBottom: 4, color: ADMIN_COLOR }}>Admin</h1>
      <p style={{ color: 'var(--text-muted)', marginBottom: 24, fontSize: 15 }}>{TAB_DESC[tab]}</p>

      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: ADMIN_BG, padding: 4, borderRadius: 10, width: 'fit-content', border: `1px solid ${ADMIN_BORDER}` }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontFamily: 'DM Sans', fontSize: 14, fontWeight: tab === t ? 600 : 400,
            background: tab === t ? 'white' : 'transparent',
            color: tab === t ? ADMIN_COLOR : 'var(--text-muted)',
            boxShadow: tab === t ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
            transition: 'all 0.15s'
          }}>{t}</button>
        ))}
      </div>

      {tab === 'Invite'  && <InviteTab />}
      {tab === 'Dry Run' && <DryRunTab />}
      {tab === 'Users'   && <UsersTab />}
      {tab === 'Manage'  && <ManageTab />}
      {tab === 'Stats'   && <StatsTab />}
    </div>
  );
}
